import { Injectable, Injector } from '@angular/core';
import { IDialogConfirmDeleteData } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete-data';
import { FiDialogConfirmDeleteComponent } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { FiItemsListHelperService } from '@fi-shared/fi-items-list/fi-items-list-helper.service';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { Observable } from 'rxjs';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiBackendService, IBackendErrorData } from '@fi-shared/fi-backend/fi-backend.service';
import { FiConfirmUserDeleteDialogComponent } from '@fi-shared/fi-confirm-user-delete-dialog/fi-confirm-user-delete-dialog.component';
import {
    MediaBankConfirmDeleteComponent,
    MediaBankConfirmDeleteData,
} from '@fi-app/media-bank/media-bank-confirm-delete/media-bank-confirm-delete.component';
import { MediaBankListData } from '@fi-app/media-bank/media-bank-list-data.service';
import { DocumentModel } from '@fi-app/document/document.model';
import { MediaBankFileModel } from '@fi-app/media-bank/media-bank-file.model';
import { IDocumentModel } from '@fi-app/document/document';

@Injectable()
export class FiItemsDeleteHelperService {
    constructor(
        private injector: Injector,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private itemsListHelperService: FiItemsListHelperService,
        private snackBarService: FiSnackBarService,
        private fiBackend: FiBackendService,
    ) {}

    commonDeleteConfirm(
        objectType: ObjectTypes,
        selectedItems: any[],
        deletePermanentlyConfirmText?: string,
    ): Promise<any[]> {
        return new Promise<any[]>((resolve, reject) => {
            const isPlural = selectedItems.length > 1;
            const itemName = this.appTextService.get(getObjectTypeNameTextId(objectType, isPlural)).toLowerCase();
            const selectedText = this.itemsListHelperService.getSelectedTextByObjectType(objectType, isPlural);
            const confirmDeleteMessage = selectedItems.some((item) => item.IsDeleted)
                ? deletePermanentlyConfirmText
                : this.appTextService.get('delete.confirm.items', {
                      '{0}': itemName,
                      '{1}': selectedText,
                  });

            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get('common.warning'),
                    messageText: confirmDeleteMessage,
                    yesButtonText: this.appTextService.get('common.delete'),
                })
                .onYesButtonClick(resolve)
                .onNoButtonClick(reject);
        });
    }

    documentDeleteConfirm(
        selectedItems: (DocumentModel | IDocumentModel)[],
        deletePermanentlyConfirmText?: string,
    ): Promise<DocumentModel[]> {
        return new Promise<DocumentModel[]>((resolve, reject) => {
            this.fiBackend
                .fetchDataPost<void>(
                    'Document/Document/CheckDocumentUsages',
                    selectedItems.map((item) => item.Id),
                )
                .then(() => {
                    this.commonDeleteConfirm(ObjectTypes.Document, selectedItems, deletePermanentlyConfirmText).then(
                        resolve,
                        reject,
                    );
                }, reject);
        });
    }

    private mediaBankDeleteConfirmInternal(
        selectedItems: MediaBankFileModel[],
        deletePermanentlyTextId: string,
    ): Promise<MediaBankFileModel[]> {
        if (selectedItems.some((item) => item.IsInUse)) {
            const dialogContentData: MediaBankConfirmDeleteData = {
                selectedItems: selectedItems,
                listData: new MediaBankListData(this.injector, { isLocalSort: true }),
            };
            return new Promise<any[]>((resolve, reject) => {
                this.fiDialogsService
                    .custom({
                        headerText: this.appTextService.get('mediaModule.select_files'),
                        yesButtonText: this.appTextService.get('common.delete'),
                        contentComponentRef: MediaBankConfirmDeleteComponent,
                        contentComponentData: dialogContentData,
                        config: {
                            panelClass: 'dialog-confirm-delete-media',
                            maxWidth: '800px',
                            width: '800px',
                        },
                    })
                    .onYesButtonClick(resolve)
                    .onNoButtonClick(reject);
            });
        } else {
            return this.commonDeleteConfirm(
                ObjectTypes.MediaBank,
                selectedItems,
                this.appTextService.get(deletePermanentlyTextId),
            );
        }
    }

    mediaBankDeleteConfirm(
        selectedItems: MediaBankFileModel[],
        deletePermanentlyConfirmText?: string,
    ): Promise<MediaBankFileModel[]> {
        return new Promise<MediaBankFileModel[]>((resolve, reject) => {
            this.fiBackend
                .fetchDataPost<void>(
                    'MediaBank/MediaBank/CheckMediaBankFileUsages',
                    selectedItems.map((item) => item.Id),
                )
                .then(() => {
                    this.mediaBankDeleteConfirmInternal(selectedItems, deletePermanentlyConfirmText).then(
                        resolve,
                        reject,
                    );
                }, reject);
        });
    }

    forumDeleteConfirm(selectedItems: any[]) {
        // note: delete routine for new forum design
        const itemsToDelete = selectedItems.slice(0);
        const deleteText1 =
            itemsToDelete.length === 1 ? 'forum.confirm_delete_forum_text' : 'forum.confirm_delete_forums_text';
        const dialogContentData: IDialogConfirmDeleteData = {
            step1: {
                message: deleteText1,
                buttonText: this.appTextService.get('common.confirm'),
                confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
            },
            step2: {
                message: 'forum.confirm_delete_forum_text_2',
                buttonText: this.appTextService.get('common.delete').toUpperCase(),
            },
        };

        return new Promise<any[]>((resolve, reject) => {
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('common.warning'),
                    contentComponentRef: FiDialogConfirmDeleteComponent,
                    contentComponentData: dialogContentData,
                    config: {
                        maxWidth: '400px',
                    },
                })
                .onYesButtonClick(() => resolve(itemsToDelete))
                .onNoButtonClick(reject);
        });
    }

    userDeleteConfirm<T extends { Id: number }>(selectedItems: T[]) {
        return new Promise<T[]>((resolve, reject) => {
            this.fiDialogsService
                .custom({
                    contentComponentRef: FiConfirmUserDeleteDialogComponent,
                    contentComponentData: {
                        userId: selectedItems[0].Id,
                        isMultipleUsersDelete: selectedItems?.length > 1,
                    },
                    headerText: this.appTextService.get('common.warning'),
                    isBigSizeTopBorder: true,
                    headerIconClass: 'red far fa-circle-exclamation',
                    colorClass: 'red',
                    yesButtonText: this.appTextService.get('common.confirm'),
                    config: {
                        panelClass: 'fi-rounded-dialog-panel',
                        maxWidth: '650px',
                        closeOnNavigation: true,
                    },
                })
                .onYesButtonClick(() => resolve(selectedItems))
                .onNoButtonClick(reject);
        });
    }

    requestAccessDeleteConfirm<T>(selectedItems: T[]) {
        const headerTextId =
            selectedItems.length === 1
                ? 'request_access.reject_request_header'
                : 'request_access.reject_requests_header';
        const messageTextId =
            selectedItems.length === 1
                ? 'request_access.reject_request_message'
                : 'request_access.reject_requests_message';
        const yesButtonTextId =
            selectedItems.length === 1
                ? 'request_access.reject_request_ok_btn'
                : 'request_access.reject_requests_ok_btn';

        return new Promise<T[]>((resolve, reject) => {
            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get(headerTextId),
                    messageText: this.appTextService.get(messageTextId),
                    yesButtonText: this.appTextService.get(yesButtonTextId),
                    config: {
                        maxWidth: '400px',
                        autoFocus: false,
                    },
                })
                .onYesButtonClick(() => resolve(selectedItems))
                .onNoButtonClick(reject);
        });
    }

    showDeletedObjectSnackBar(
        objectType: ObjectTypes,
        deletedItems: any[],
        isPermanentDelete: boolean,
        restoreObjectFn: (restoreIds: number[]) => IFiHttpPromise<any>,
        reloadListFn?: () => void,
    ) {
        const textMessage =
            `${deletedItems.length} ` +
            `${this.appTextService.get(getObjectTypeNameTextId(objectType, deletedItems.length > 1))} ` +
            `${
                isPermanentDelete
                    ? this.appTextService.get('history.deleted').toLowerCase()
                    : this.appTextService.get('history.moved_to_trash').toLowerCase()
            }`;

        const buttonText = isPermanentDelete
            ? this.appTextService.get('common.dismiss')
            : this.appTextService.get('common.undo');

        this.showSnackBar(textMessage, buttonText).subscribe(() => {
            this.onSnackBarActionClick(objectType, deletedItems, isPermanentDelete, restoreObjectFn, reloadListFn);
        });
    }

    showDeletedObjectSimpleSnackBar(objectType: ObjectTypes, deletedItemIds: number[], itemName: string) {
        const isPlural = deletedItemIds.length !== 1;
        const objectName = this.appTextService.get(getObjectTypeNameTextId(objectType, isPlural));
        const textMessage = !isPlural
            ? this.appTextService.get('common.item_has_been_deleted', { '{0}': itemName ?? objectName })
            : this.appTextService.get('common.items_have_been_deleted', {
                  '{0}': objectName.toLowerCase(),
                  '{n}': deletedItemIds.length,
              });

        const buttonText = this.appTextService.get('common.dismiss');

        this.snackBarService.showSnackbar({
            textMessage: textMessage,
            buttonText: buttonText,
        });
    }

    private showSnackBar(textMessage: string, buttonText: string): Observable<any> {
        return this.snackBarService
            .showSnackbar({
                textMessage: textMessage,
                buttonText: buttonText,
            })
            .onAction();
    }

    private onSnackBarActionClick(
        objectType: ObjectTypes,
        deletedItems: any[],
        isPermanentDelete: boolean,
        restoreObjectFn: (restoreIds: number[]) => IFiHttpPromise<any>,
        reloadListFn: () => void,
    ) {
        if (!isPermanentDelete) {
            const textMessage =
                `${deletedItems.length} ` +
                `${this.appTextService.get(getObjectTypeNameTextId(objectType, deletedItems.length > 1))} ` +
                `${this.appTextService.get('history.restored').toLowerCase()}`;

            restoreObjectFn(deletedItems.map((item) => item.Id)).then(() => {
                reloadListFn();
                this.showSnackBar(textMessage, this.appTextService.get('common.dismiss'));
            });
        }
    }

    restoreItems<T extends { Id?: number }>(args: {
        itemsToRestore: T[];
        objectType: ObjectTypes;
        restoreFn: () => IFiHttpPromise<void>;
        afterRestoreFn: (restoredItems: T[]) => void;
    }) {
        let errorResponse: IBackendErrorData;
        args.restoreFn()
            .catch((response) => {
                errorResponse = response;
            })
            .finally(() => {
                const failedIds: number[] = errorResponse?.error?.Data?.ObjectId
                    ? errorResponse?.error?.Data?.ObjectId
                    : [];

                const restoredItems = args.itemsToRestore.filter((item) => failedIds.indexOf(item.Id) === -1);

                const textMessage =
                    restoredItems.length +
                    ' ' +
                    this.appTextService
                        .get(getObjectTypeNameTextId(args.objectType, restoredItems.length !== 1))
                        .toLowerCase() +
                    ' ' +
                    this.appTextService.get('history.restored').toLowerCase();

                this.snackBarService.showSnackbar({
                    textMessage: textMessage,
                    buttonText: this.appTextService.get('common.dismiss'),
                });

                args.afterRestoreFn(restoredItems);
            });
    }
}
